import { ISOToEpochTime } from '@sky-it-cmscomposer/liveblog-widgets';
import { IPost as IPostDS, IPinnedPost as IPinnedPostDS } from '@sky-it-cmscomposer/liveblog-design-system';

/**
 * Returns the shallow copy without the removed element at index
 *
 * @param posts
 * @param post
 * @returns Array<IPostDS>
 */
export const removePost = (posts: IPostDS[] = [], post: IPostDS): IPostDS[] => posts.filter((currentPost) => currentPost.postId !== post.postId);

/**
 * Returns the IN-PLACE modified array with the element inserted in the new position based on datetime
 *
 * @param originalPostList
 * @param post
 * @returns Array<IPostDS>
 */
const insertPostAtDescDatetimeOrder = (originalPostList: IPostDS[], post: IPostDS): IPostDS[] => {
    // Found the element in the array, we proceed with DELETETION of the element in the old position...
    const updatedPostList = removePost(originalPostList, post);

    // ...then occurs the insertion
    const foundIndex = updatedPostList.findIndex((currentpost) => ISOToEpochTime(currentpost.timestamp) < ISOToEpochTime(post.timestamp));
    const insertAtIndex = foundIndex === -1 ? updatedPostList.length : foundIndex;
    updatedPostList.splice(insertAtIndex, 0, post);

    return updatedPostList;
};

/**
 * Support function to handle post adding complexity in various cases
 *
 * @param { posts: IPostDS[], pinnedPosts: IPinnedPostDS[], post: IPostDS | IPinnedPostDS }
 * @returns { posts: IPostDS[], pinnedPosts: IPinnedPostDS[] }
 */
export const postIntoListComplexityHandler = ({
    posts = [],
    pinnedPosts = [],
    post,
}: {
    posts: IPostDS[];
    pinnedPosts?: IPinnedPostDS[];
    post: IPostDS | IPinnedPostDS;
}) => {
    /**
     * CASE LEGEND:
     * [case A]: standard published post received -> we update the posts with insertion in the right position
     * [case B]: pinned post received -> we insert in pinnedPosts at the right position
     * [case C]: standard post became pinned -> we insert in pinnedPosts at the right position, then we remove from main posts
     * [case D]: updated post is oldest then the oldest post currently showed, so we have to discard that update
     */
    const MAX_ITEMS_ALLOWED = 5;
    if (post.isPinned) {
        // [case B] insert into pinned
        const newPinnedList = insertPostAtDescDatetimeOrder([...pinnedPosts], post);

        // [case C] standard post became pinned post
        const substituteAtIndex = posts.findIndex((currentpost) => currentpost.postId === post.postId); // search and remove from posts if is becomed a pinned post
        if (substituteAtIndex !== -1) {
            posts.splice(substituteAtIndex, 1);
        }

        return {
            pinnedPosts: newPinnedList,
            posts: [...posts],
        };
    } else {
        // [case D]: case if the updated post is out of currently showed post array or array is empty
        const oldestShowedPost = posts[posts.length - 1];

        if (posts.length > MAX_ITEMS_ALLOWED && ISOToEpochTime(post.timestamp) < ISOToEpochTime(oldestShowedPost.timestamp)) {
            return {};
        }

        // [case A] insert at array index taking advantage of encapsulated logic inside this function
        const newPostList = insertPostAtDescDatetimeOrder([...posts], post);
        // CHECK: if the element is present in pinnedPosts and we unpin it
        const newPinnedList = removePost([...pinnedPosts], post);
        return {
            pinnedPosts: newPinnedList,
            posts: newPostList,
        };
    }
};
